import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputBase,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import FormStatusAlert from "../../components/FormStatusAlert";
import SignatureControls from "../../components/SignatureControls";
import { copyTextSilently } from "../../../../../../utils/copyTextSilently";

const PDFEditorHeader = ({
  title,
  onTitleChange,
  headerFormId,
  onSave,
  onClose,
  onFinalizeSuccess,
  loading,
  setOpenPreviewDialog,
  formData,
  isNew,
  openSettingsDialog,
  setOpenSettingsDialog,
  onUpdate,
  editLockLost = false,
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title || "Untitled Form");
  const [openStatusAlert, setOpenStatusAlert] = useState(false);
  const formId =
    formData?.form_id ||
    formData?.formId ||
    formData?.form?.form_id ||
    headerFormId;

  useEffect(() => {
    setDraftTitle(title || "Untitled Form");
  }, [title]);

  const commitTitle = () => {
    const finalTitle = draftTitle?.trim() || "Untitled Form";
    onTitleChange?.(finalTitle);
    setDraftTitle(finalTitle);
    setIsEditingTitle(false);
  };

  const handleCloseStatusAlert = () => {
    setOpenStatusAlert(false);
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: "white",
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
          flexShrink: 0,
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              minWidth: 0,
            }}
          >
            {isEditingTitle ? (
              <InputBase
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitTitle();
                  if (e.key === "Escape") {
                    setDraftTitle(title || "Untitled Form");
                    setIsEditingTitle(false);
                  }
                }}
                autoFocus
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  borderBottom: "1px solid #1976d2",
                  minWidth: 220,
                }}
              />
            ) : (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title || "Untitled Form"}
              </Typography>
            )}

            <IconButton
              size="small"
              onClick={() =>
                isEditingTitle ? commitTitle() : setIsEditingTitle(true)
              }
            >
              {isEditingTitle ? (
                <CheckIcon fontSize="small" />
              ) : (
                <EditIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
          <SignatureControls
            signatures={
              formData?.updated_settings?.signatures ||
              formData?.guessedFormDetails?.signatures
            }
            onUpdate={onUpdate}
            disabled={loading || editLockLost}
          />
          {formId && (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ pt: 0.75 }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: "#222222" }}
              >
                Form ID
              </Typography>
              <Tooltip title="Copy form ID">
                <Chip
                  size="small"
                  clickable
                  label={formId}
                  onClick={() => copyTextSilently(formId)}
                  aria-label={`Copy form ID ${formId}`}
                  sx={{
                    height: 22,
                    fontSize: 12,
                    fontWeight: 500,
                    bgcolor: "#e6f4ea",
                    color: "#1e7e34",
                    border: "1px solid #34a853",
                    "& .MuiChip-icon": {
                      color: "#1e7e34",
                    },
                    "&:hover": {
                      bgcolor: "#e6f4ea",
                      borderColor: "#34a853",
                    },
                  }}
                />
              </Tooltip>
            </Stack>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            onClick={() => setOpenSettingsDialog(true)}
            disabled={loading || editLockLost}
          >
            Document Settings
          </Button>
          <Button
            variant="contained"
            onClick={() => setOpenPreviewDialog(true)}
            disabled={loading || editLockLost}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            onClick={() => onSave(false)}
            disabled={loading || editLockLost}
            sx={{ width: "max-content" }}
          >
            Save Changes
          </Button>
          {(formData?.status === "inactive" || isNew) && (
            <Button
              variant="contained"
              onClick={() => setOpenStatusAlert(true)}
              disabled={loading || editLockLost}
              sx={{ bgcolor: "green", width: "max-content" }}
            >
              Finalize Form
            </Button>
          )}
          <IconButton
            onClick={onClose}
            disabled={loading && !editLockLost}
            aria-label="Close editor"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <FormStatusAlert
        status={"active"}
        open={openStatusAlert}
        handleClose={handleCloseStatusAlert}
        formId={formData?.form_id || formData?.formId}
        beforeStatusChange={() => onSave(false, { silent: true })}
        fetchData={onFinalizeSuccess || onClose}
      />
    </>
  );
};

export default PDFEditorHeader;
